import { ReactElement, useEffect } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import { LatLngBounds, Map as LeafletMap } from 'leaflet';

// Leaflet's getBoundsZoom returns Infinity for zero-extent bounds (a single marker, or
// coincident markers) and for a container not yet laid out. fitBounds then calls
// setView(center, Infinity), which is only clamped by the map's maxZoom -- and that is
// still Infinity here, because FitBounds' effect runs before the sibling TileLayer has
// been added. A numeric maxZoom on the call itself is what keeps the map's zoom finite.
const FIT_BOUNDS_MAX_ZOOM = 17;

export function FitBounds({
  bounds,
  trigger,
}: {
  bounds: LatLngBounds | null;
  trigger: number;
}): ReactElement | null {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: FIT_BOUNDS_MAX_ZOOM });
    }
  }, [map, bounds, trigger]);

  return null;
}

export function BoundsListener({
  onBoundsChange,
}: {
  onBoundsChange: (shouldShow: boolean) => void;
}): ReactElement | null {
  const map = useMapEvents({
    zoomend: () => handleBoundsChange(map, onBoundsChange),
    moveend: () => handleBoundsChange(map, onBoundsChange),
  });

  useEffect(() => {
    handleBoundsChange(map, onBoundsChange);
  }, [map, onBoundsChange]);

  return null;
}

function handleBoundsChange(map: LeafletMap, onBoundsChange: (shouldShow: boolean) => void): void {
  const bounds = map.getBounds();
  if (!bounds.isValid()) return;
  const diagonalDistanceMeters = bounds.getNorthEast().distanceTo(bounds.getSouthWest());
  onBoundsChange(diagonalDistanceMeters <= 10_000);
}
